import type { ReactNode } from 'react';
import { ArrowDownIcon, ArrowUpIcon, Card, CardContent } from '@forge/common';
import { AreaChart } from '@forge/charts';
import type { SeriesAreaOptions } from 'highcharts';

export interface KpiTrend {
  label: string;
  direction: 'up' | 'down' | 'neutral';
}

interface KpiCardProps {
  icon: ReactNode;
  value: string | number;
  label: string;
  accentColor: string;
  trend?: KpiTrend;
  sparkline?: number[];
  detail?: ReactNode;
}

const Sparkline = ({ data, color }: { data: number[]; color: string }) => {
  const series: SeriesAreaOptions[] = [
    {
      type: 'area',
      data,
      color,
      fillOpacity: 0.16,
      lineWidth: 2,
      marker: { enabled: false },
      enableMouseTracking: false,
      states: { hover: { enabled: false } },
    },
  ];

  return (
    <AreaChart
      series={series}
      chart={{
        height: 48,
        margin: [4, 0, 0, 0],
        backgroundColor: 'transparent',
      }}
      xAxis={{ visible: false }}
      yAxis={{ visible: false, startOnTick: false, endOnTick: false }}
      legend={{ enabled: false }}
      tooltip={{ enabled: false }}
      accessibility={{ enabled: false }}
    />
  );
};

const trendStyles: Record<KpiTrend['direction'], string> = {
  up: 'text-green-600 dark:text-green-400',
  down: 'text-red-600 dark:text-red-400',
  neutral: 'text-color-tertiary',
};

const KpiCard = ({
  icon,
  value,
  label,
  accentColor,
  trend,
  sparkline,
  detail,
}: KpiCardProps) => {
  return (
    <Card className="relative h-full overflow-hidden border border-gray-200/80 shadow-sm dark:border-gray-800">
      <div
        className="absolute inset-x-0 top-0 h-1"
        style={{ backgroundColor: accentColor }}
      />
      <CardContent className="flex h-full flex-col gap-4 p-5">
        <div className="flex items-start justify-between gap-3">
          <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-gray-100 shadow-sm dark:bg-gray-800">
            {icon}
          </div>
          {trend ? (
            <span
              className={`inline-flex items-center gap-1 text-xs font-semibold ${trendStyles[trend.direction]}`}
            >
              {trend.direction === 'up' ? (
                <ArrowUpIcon className="h-3.5 w-3.5" aria-hidden />
              ) : undefined}
              {trend.direction === 'down' ? (
                <ArrowDownIcon className="h-3.5 w-3.5" aria-hidden />
              ) : undefined}
              {trend.label}
            </span>
          ) : undefined}
        </div>

        <div className="space-y-1">
          <p className="text-3xl font-bold tracking-tight text-gray-900 dark:text-gray-100 md:text-4xl">
            {value}
          </p>
          <p className="text-sm font-medium text-color-tertiary">{label}</p>
        </div>

        <div className="mt-auto">
          {sparkline && sparkline.length > 0 ? (
            <Sparkline data={sparkline} color={accentColor} />
          ) : (
            detail
          )}
        </div>
      </CardContent>
    </Card>
  );
};

export type { KpiCardProps };
export default KpiCard;
